import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Accordion, HTMLParser, IAccordionItem, Image, PageHeader, PageSection, StartInkindCard } from '@gik/ui';
import MarketingNavSection from '../../components/MarketingNav/MarketingNavSection';
import FeatureContent from '../../components/FeatureContent';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';
import i18n from '@gik/i18n';
import TipsSamplePagesSection from './TipsSamplePagesSection';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import TestimonialsCarouselSection from '@gik/ui/gik/TestimonialsCarouselSection';
import { CreateClickedLocation } from '@gik/create/hooks/useCreateAnalytics';

function createAccordionItems(prefix: string, totalItems: number): IAccordionItem[] {
  return [...Array(totalItems)].map((_, index) => ({
    title: HTMLParser({ rawHtml: i18n.t(translationKeys[`${prefix}${index + 1}Title`]) }),
    content: HTMLParser({ rawHtml: i18n.t(translationKeys[`${prefix}${index + 1}Content`]) }),
  }));
}

const blockName = 'tips-sample-page';

export default function TipsSamplePage(): React.ReactElement {
  const bem = bemBlock(blockName);
  const { t } = useTranslation();

  const organizersFAQItems: IAccordionItem[] = React.useMemo(() => createAccordionItems('organizersFAQ', 7), []);
  const supportersFAQItems: IAccordionItem[] = React.useMemo(() => createAccordionItems('supportersFAQ', 4), []);
  const recipientsFAQItems: IAccordionItem[] = React.useMemo(() => createAccordionItems('recipientsFAQ', 4), []);

  return (
    <div className={bem()}>
      <PageHeader variant="neutral" decoration="foliage">
        <h1>{t(translationKeys.title)}</h1>
      </PageHeader>

      <PageSection className={bem('lead')} noPadBottom>
        <p className="gik-section-lead">{t(translationKeys.leadDescription)}</p>
      </PageSection>

      <PageSection className={bem('section-features')}>
        <div className={bem('content')}>
          <FeatureContent
            variant="secondary"
            title={
              <div className={bem('feature_title')}>
                <Image
                  className={bem('feature_image')}
                  src={[
                    `${getAssetsUrl()}marketing/icons/individual.png`,
                    `${getAssetsUrl()}marketing/icons/individual@2x.png 2x`,
                  ]}
                />
                <br className="xs" />
                <span>{t(translationKeys.organizersTitle)}</span>
              </div>
            }
            titleCentered
            description={t(translationKeys.organizersDescription)}
            headingLevel={2}
          >
            <Accordion headingLevel={3} items={organizersFAQItems} dividers />
          </FeatureContent>
          <div className={bem('foliage-mobile', 'right')} />
          <FeatureContent
            variant="blue"
            title={
              <div className={bem('feature_title')}>
                <Image
                  className={bem('feature_image')}
                  src={[
                    `${getAssetsUrl()}marketing/icons/supporter.png`,
                    `${getAssetsUrl()}marketing/icons/supporter@2x.png 2x`,
                  ]}
                />
                <br className="xs" />
                <span>{t(translationKeys.supportersTitle)}</span>
              </div>
            }
            titleCentered
            description={t(translationKeys.supportersDescription)}
            headingLevel={2}
          >
            <Accordion headingLevel={3} items={supportersFAQItems} dividers />
          </FeatureContent>
          <div className={bem('foliage-mobile', 'left')} />
          <FeatureContent
            variant="danger"
            title={
              <div className={bem('feature_title')}>
                <Image
                  className={bem('feature_image')}
                  src={[`${getAssetsUrl()}marketing/icons/love.png`, `${getAssetsUrl()}marketing/icons/love@2x.png 2x`]}
                />
                <br className="xs" />
                <span>{t(translationKeys.recipientsTitle)}</span>
              </div>
            }
            titleCentered
            description={t(translationKeys.recipientsDescription)}
            headingLevel={2}
          >
            <Accordion headingLevel={3} items={recipientsFAQItems} dividers />
          </FeatureContent>
        </div>
      </PageSection>

      <TipsSamplePagesSection />
      <TestimonialsCarouselSection />
      <StartInkindCard trackingId={CreateClickedLocation.TIPS_SAMPLE_PAGES_PAGE} />
      <br className="xl" />
      <br className="xl" />
      <MarketingNavSection variant="neutral" />
    </div>
  );
}
